import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../components/authContext";
import { onAuthStateChanged } from "firebase/auth";
import { auth } from "../components/firebaseConfig";
import ABActivityModal from "../components/ABActivityModal";
import { TimeSelect } from "../components/TimeSelect";
import "./CreateListing.css";
import { CameraAlt, AccountCircle } from '@mui/icons-material';
import {
  APIProvider
} from '@vis.gl/react-google-maps';
import ABLocationAutocomplete from "../components/ABLocationAutoComplete";

// ---- Config ----
const API_BASE_URL = process.env.REACT_APP_BASE_URL; // e.g., http://localhost:5000
const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_PLACES_KEY;

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const MIN_SLOT_MINUTES = 30;

function buildTimeOptions(stepMinutes = 30) {
  const opts = [];
  for (let m = 0; m < 24 * 60; m += stepMinutes) {
    const hh = Math.floor(m / 60);
    const mm = m % 60;

    const value = `${String(hh).padStart(2, "0")}:${String(mm).padStart(2, "0")}`;

    const ampm = hh >= 12 ? "PM" : "AM";
    const hour12 = ((hh + 11) % 12) + 1;
    const label = `${hour12}:${String(mm).padStart(2, "0")}${ampm}`;

    opts.push({ value, label });
  }
  return opts;
}

function toMinutes(hhmm) {
  if (!hhmm) return null;
  const [h, m] = hhmm.split(":").map(Number);
  return h * 60 + m;
}


export default function CreateListing() {
  const { user } = useAuth();              // firebase user from your AuthContext
  const navigate = useNavigate();
  const location = useLocation();
  const [authReady, setAuthReady] = useState(false);

  // One-time auth gate: redirect home if *definitely* signed out
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (fbUser) => {
      setAuthReady(true);
      if (!fbUser) {
        navigate("/", { replace: true, state: { from: location.pathname } });
      }
    });
    return () => unsub();
  }, [navigate, location.pathname]);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const browserTimeZone = useMemo(() => Intl.DateTimeFormat().resolvedOptions().timeZone, []);

  // form state
  const [displayName, setDisplayName] = useState("");
  const [bio, setBio] = useState("");
  const [profilePicFile, setProfilePicFile] = useState(null);
  const [profilePicPreview, setProfilePicPreview] = useState("");
  const [inviteCode, setInviteCode] = useState("");



  // activities (stub list)
  const [activities, setActivities] = useState([]);
  const [showActivityModal, setShowActivityModal] = useState(false);
  const [activityToEdit, setActivityToEdit] = useState(null);




  const [availabilityMode, setAvailabilityMode] = useState("anytime"); // 'anytime' | 'custom'
  const [weeklyAvailability, setWeeklyAvailability] = useState(() =>
    Object.fromEntries(DAYS.map(d => [d, { start: "", end: "" }]))
  );




  // ---- Guard + Prefill ----
  useEffect(() => {
    if (!user) return; // wait for auth
    let alive = true;
    (async () => {
      try {
        const token = await user.getIdToken();
        const res = await fetch(`${API_BASE_URL}/api/create/prevalidation`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const json = await res.json();
        if (!res.ok) throw new Error(json?.error || "Failed to load profile.");
        if (!alive) return;

        // Gate: if already created, send them onward (e.g. dashboard)
        if (json.profileCreated) {
          navigate("/dashboard"); // or /dashboard
          return;
        }

        // prefill
        setDisplayName(json.displayName || "");
      } catch (e) {
        if (alive) setError(e.message);
      } finally { /* no-op */ }
    })();
    return () => { alive = false; };
  }, [user, navigate]);

  const [address, setAddress] = useState("");
  const [locationLat, setLocationLat] = useState(null);
  const [locationLng, setLocationLng] = useState(null);

  const [showValidation, setShowValidation] = useState(false);

  const validationErrors = useMemo(() => {
    const errors = {};
    if (!profilePicFile) errors.profilePic = "Please upload a profile photo.";
    if (!displayName.trim()) errors.displayName = "Display name is required.";
    if (!bio.trim()) errors.bio = "Please add a short bio.";
    if (!address.trim()) errors.address = "Please add your city.";
    if (activities.length === 0) errors.activities = "Add at least one activity.";
    return errors;
  }, [profilePicFile, displayName, bio, address, activities]);

  const isValid = Object.keys(validationErrors).length === 0;



  // ---- Handlers ----
  const onPicChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => setProfilePicPreview(reader.result);
    reader.readAsDataURL(f);
    setProfilePicFile(f);
  };




  const handleAddOrEditActivity = (data) => {
    setActivities(prev => {
      const idx = prev.findIndex(a => a.id === data.id);
      if (idx > -1) {
        const copy = [...prev];
        copy[idx] = data;
        return copy;
      } else {
        return [...prev, data];
      }
    });
  };

  const handleDeleteActivity = (id) => {
    setActivities(prev => prev.filter(a => a.id !== id));
  };

  const parseDuration = (str) => {
    if (!str) return null;
    const [num, unit] = str.split(" ");
    const minutes = parseFloat(num) * (unit.startsWith("hour") ? 60 : 1);
    return Math.round(minutes);
  };

  function getExtraCostType(options) {
    if (options.separate) return 'paid_separately';
    return 'included';
  }


  const onSubmit = async (e) => {
    e.preventDefault();
    setShowValidation(true);
    setError("");
    if (!isValid) {
      return;
    }
    try {
      setSaving(true);
      if (availabilityMode === "custom") {
        for (const d of DAYS) {
          const { start, end } = weeklyAvailability[d] || {};
          // Must have end if there's a start
          if (start && !end) {
            setError(`Please select an end time for ${d}.`);
            return;
          }
          // Must be >= 30 min after start if both present
          if (start && end && toMinutes(end) < toMinutes(start) + MIN_SLOT_MINUTES) {
            setError(`End time on ${d} must be at least 30 minutes after the start.`);
            return;
          }
        }
      }
      const cleanedActivities = activities.map((a) => ({
        title: a.title,
        description: a.desc,
        durationMinutes: parseDuration(a.duration),
        price: parseFloat(a.price),
        extraNotes: a.extraNotes || "",
        extraCost: getExtraCostType(a.costOptions),
        address: a.location
      }));
      const token = await user.getIdToken();
      const formData = new FormData();
      formData.append('profilePhoto', profilePicFile);

      const data = {
        displayName: displayName.trim(),
        bio: bio.trim(),
        address: address.trim(),
        activities: JSON.stringify(cleanedActivities),
        availability: JSON.stringify({
          mode: availabilityMode,            // 'anytime' | 'custom'
          weekly: weeklyAvailability         // { Mon:{start:'HH:mm', end:'HH:mm'}, ... }
        }),
        inviteCode: inviteCode.trim(),
        profileCreated: true,
        timeZone: browserTimeZone
      }
      Object.keys(data).forEach((key) => {
        formData.append(key, data[key]);
      });
      if (locationLat != null) formData.append("locationLat", locationLat);
      if (locationLng != null) formData.append("locationLng", locationLng);

      // Save profile
      const res = await fetch(`${API_BASE_URL}/api/submit/review`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`
        },
        body: formData
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json?.error || "Could not submit your application.");
      // go wherever creators land after submission
      navigate("/dashboard");
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };



  const timeOptions = useMemo(() => buildTimeOptions(30), []);

  const timeOptionsEnd = useMemo(
    () => [...timeOptions, { value: "24:00", label: "12:00AM (next day)" }],
    [timeOptions]
  );

  const handleAvailChange = (day, field, value) => {
    setWeeklyAvailability(prev => {
      const current = prev[day] || { start: "", end: "" };
      let next = { ...current, [field]: value };

      if (field === "start") {
        const sMin = toMinutes(value);
        const eMin = toMinutes(current.end);
        if (sMin != null) {
          const minAllowedEnd = sMin + MIN_SLOT_MINUTES;
          if (eMin == null || eMin < minAllowedEnd) {
            // clear or bump end to the minimum allowed if you prefer.
            next.end = ""; // simplest: force user to re-pick a valid end
            // OR auto-bump:
            // next.end = minutesToHHMM(minAllowedEnd);
          }
        } else {
          // If start cleared, also clear end
          next.end = "";
        }
      }

      return { ...prev, [day]: next };
    });
  };

  function getEndOptionsForDay(day) {
    const start = weeklyAvailability[day]?.start || "";
    if (!start) return timeOptionsEnd;
    const minEndMins = toMinutes(start) + MIN_SLOT_MINUTES;
    return timeOptionsEnd.filter(opt => toMinutes(opt.value) >= minEndMins);
  }

  function hasAvailRowError(day) {
    const row = weeklyAvailability[day] || { start: "", end: "" };
    // Error when a start exists but no end yet
    return !!row.start && !row.end;
  }


  if (!authReady) {
    return (
      <div className="cl-create-container">
        <div className="cl-create-card">Loading…</div>
      </div>
    );
  }
  if (!user) return null;

  return (
    <APIProvider apiKey={GOOGLE_MAPS_API_KEY} libraries={['places']}>
      <div className="cl-create-container">
        <div className="cl-create-card">
          <h1 className="cl-create-title">Let’s Set Up Your Page</h1>

          {/* Your Details */}
          <section className="cl-create-section">
            <h3 className="cl-section-title">Your Details</h3>
            <p className="cl-section-hint">Add your photo, name, bio, and city</p>

            <div className="cl-centered-fields">
              {/* 1. Profile Photo */}
              <div className="cl-row">
                <label className="cl-row-label">Profile Photo</label>
                <div className="cl-row-body">
                  <div className="cl-profile-photo-upload">
                    <div className="cl-photo-circle">
                      {profilePicPreview ? (
                        <img src={profilePicPreview} alt="Profile" className="cl-profile-photo-preview" />
                      ) : (
                        <AccountCircle className="cl-photo-icon" fontSize="large" />
                      )}
                    </div>
                    <label className="cl-upload-button">
                      <CameraAlt className="cl-camera-icon" />
                      <span>Upload</span>
                      <input
                        type="file"
                        accept="image/jpeg, image/jpg, image/png"
                        onChange={onPicChange}
                        className="cl-file-input"
                      />
                    </label>
                  </div>

                </div>
                    {showValidation && validationErrors.profilePic && (
                    <div className="cl-field-error">{validationErrors.profilePic}</div>
                  )}
              </div>

              {/* 2. Display Name */}
              <div className="cl-row">
                <label className="cl-row-label">Display Name</label>
                <div className="cl-row-body cl-input-wrapper">
                  <input
                    className={`cl-text-input ${showValidation && validationErrors.displayName ? "cl-input-error" : ""}`}
                    value={displayName}
                    placeholder="e.g. Sarah"
                    onChange={(e) => setDisplayName(e.target.value)}
                    maxLength={30}
                  />
                  {showValidation && validationErrors.displayName && (
                    <div className="cl-field-error">{validationErrors.displayName}</div>
                  )}
                </div>
              </div>

              {/* 3. Bio / Intro */}
              <div className="cl-row">
                <label className="cl-row-label">Bio </label>
                <div className="cl-row-body cl-input-wrapper">
                  <div className="cl-textarea-wrapper">
                    <textarea
                      className={`cl-textarea ${showValidation && validationErrors.bio ? "cl-input-error" : ""}`}
                      value={bio}
                      onChange={(e) => setBio(e.target.value)}
                      maxLength={150}
                      rows={4}
                    />
                    <span className="cl-char-counter">{bio.length}/150</span>
                  </div>
                  {showValidation && validationErrors.bio && (
                    <div className="cl-field-error">{validationErrors.bio}</div>
                  )}
                </div>
              </div>

              {/* 4. Location */}
              <div className="cl-row">
                <label className="cl-row-label">Location</label>
                <div className="cl-row-body cl-input-wrapper">
                  <ABLocationAutocomplete
                    value={address}
                    inputClassName={`cl-text-input ${showValidation && validationErrors.address ? "cl-input-error" : ""}`}
                    onSelect={(place) => {
                      setAddress(place.formattedAddress || place.displayName?.text || '');
                      setLocationLat(place.lat ?? null);
                      setLocationLng(place.lng ?? null);
                    }}
                  />
                  {showValidation && validationErrors.address && (
                    <div className="cl-field-error">{validationErrors.address}</div>
                  )}
                </div>
              </div>

            </div>
          </section>

          {/* Activities */}
          <section className="cl-create-section">
            <h3 className="cl-section-title">What Can People Book You For?</h3>
            <p className="cl-section-hint">Activities you offer (walks, yoga, dinners, matchas, chats, etc)</p>
            <div className="cl-centered-fields cl-centered-activity-box">
              {activities.map(a => (
                <div className="cl-activity-wrapper" key={a.id}>
                  <button
                    className="cl-delete-button"
                    onClick={() => handleDeleteActivity(a.id)}
                  >x</button>

                  <div className="cl-activity-card">

                    <div className="cl-activity-left">
                      <div className="cl-activity-title">{a.title}</div>
                      <div className="cl-activity-meta">
                        {`$${Number(a.price || 0).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`} • {a.duration}
                      </div>
                      <div className="cl-activity-blurb">{a.blurb}</div>
                    </div>
                    <button
                      className="cl-btn-tertiary"
                      onClick={() => {
                        setActivityToEdit(a);
                        setShowActivityModal(true);
                      }}
                    >
                      Edit
                    </button>
                  </div>
                </div>
              ))}

              <div className="cl-center-button">
                <button
                  className="cl-btn-secondary"
                  onClick={() => {
                    setActivityToEdit(null);
                    setShowActivityModal(true);
                  }}
                >
                  Add New Activity
                </button>
              </div>
              {showValidation && validationErrors.activities && (
                <div className="cl-field-error">{validationErrors.activities}</div>
              )}
              <ABActivityModal
                open={showActivityModal}
                onClose={() => setShowActivityModal(false)}
                onSubmit={handleAddOrEditActivity}
                initialData={activityToEdit || {}}
              />
            </div>
          </section>

          {/* Availability */}
          <section className="cl-create-section">
            <h3 className="cl-section-title">When Are You Available?</h3>
            <p className="cl-section-hint">Set your schedule so you get booked at times that work for you.</p>

            <div className="cl-centered-fields cl-availability-wrap">
              <div className="cl-availability-choices">
                <label className="cl-bullet-option">
                  <input
                    type="radio"
                    name="availabilityMode"
                    value="anytime"
                    checked={availabilityMode === "anytime"}
                    onChange={() => setAvailabilityMode("anytime")}
                  />
                  <span className="cl-time-bullet-text">Anytime — You're always available unless already booked</span>
                </label>

                <label className="cl-bullet-option">
                  <input
                    type="radio"
                    name="availabilityMode"
                    value="custom"
                    checked={availabilityMode === "custom"}
                    onChange={() => setAvailabilityMode("custom")}
                  />
                  <span className="cl-time-bullet-text">Custom — Pick specific days/times</span>
                </label>
              </div>

              {availabilityMode === "custom" && (
                <>
                  <p className="cl-section-hint cl-hint-sub">
                    Not entering a start time and end time for a day means you're not available that day.
                  </p>

                  <div className="cl-availability-grid">
                    {/* Header row */}
                    <div className="cl-avail-day-header"></div>
                    <div className="cl-avail-col-header">Start Time</div>
                    <div className="cl-avail-col-header">End Time</div>

                    {DAYS.map((day) => {
                      const rowError = hasAvailRowError(day);
                      return (
                        <React.Fragment key={day}>
                          <div className="cl-avail-day-label">{day}</div>

                          <div>
                            <TimeSelect
                              value={weeklyAvailability[day].start}
                              options={timeOptions}
                              onChange={(val) => handleAvailChange(day, "start", val)}
                              allowClear
                            />
                          </div>

                          <div>
                            <TimeSelect
                              value={weeklyAvailability[day].end}
                              options={getEndOptionsForDay(day)}
                              onChange={(val) => handleAvailChange(day, "end", val)}
                              disabled={!weeklyAvailability[day].start}
                              error={rowError}
                              allowClear
                            />
                            {rowError && (
                              <div className="cl-field-error">Please choose an end time.</div>
                            )}
                          </div>
                        </React.Fragment>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          </section>



          {/* Invite Code (Optional) */}
          <section className="cl-create-section">
            <h3 className="cl-section-title">Invite Code (Optional)</h3>
            <p className="cl-section-hint">
              Codes are shared by ActivityBuddy creators and partners. Adding one helps you skip the line in the approval process.
            </p>
            <div className="cl-centered-fields">
              <div className="cl-input-wrapper">
                <input
                  className="cl-text-input"
                  value={inviteCode}
                  onChange={(e) => setInviteCode(e.target.value)}
                  placeholder="Enter your invite code"
                  maxLength={30}
                />
              </div>
            </div>
          </section>

          {error && <div className="cl-error-text">{error}</div>}

          <div className="cl-footer-actions">
            <button
              className="cl-btn-primary"
              aria-disabled={!isValid || saving}
              disabled={saving}
              onClick={onSubmit}
            >
              {saving ? "Submitting…" : "Submit for Review"}
            </button>
          </div>
        </div>
      </div>
    </APIProvider>
  );
}
